import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { ClientMaster, ReplacementPreview } from '@Models';
import { bopsApi } from '@Network';

/**
 * What the server said went wrong, or a sentence of ours.
 *
 * Every refusal these endpoints make is something the person can act on
 * — the name is taken, the file is open, it is not the file they
 * approved — so the server's own wording is better than anything
 * generic. The fallback is for the failures that reach no handler.
 *
 * Read off the shape rather than through the transport library: which
 * one carries the request is `bopsApi`'s business, and a hook that knows
 * is a hook that has to change when that does. A `detail` that is not a
 * sentence is ignored, because a validation failure answers with a list
 * of objects and showing it reads as "[object Object]".
 */
function messageFor(error: unknown, fallback: string): string {
  const refused = error as { response?: { data?: { detail?: unknown } } } | null;
  const detail = refused?.response?.data?.detail;

  if (typeof detail === 'string' && detail.length > 0) return detail;

  return fallback;
}

/**
 * The master operations a client can perform on their own files.
 *
 * Here rather than in the components so that what reaches a view is a
 * promise and a sentence: the view decides what to show, and nothing
 * about how the request is shaped or how a refusal is read is written
 * twice.
 *
 * Each one keeps its identity between renders, so a caller can put it in
 * a dependency list -- the table's columns are memoised against the
 * handlers that close over these.
 */
export default function useMasters() {
  const { t } = useTranslation();

  const createMaster = useCallback(
    async (file: File, masterType: string, sourceId: string | null): Promise<ClientMaster> => {
      try {
        return await bopsApi.createMaster(file, masterType, sourceId);
      } catch (error) {
        throw new Error(messageFor(error, t('Configuration.NewMasterError')));
      }
    },
    [t],
  );

  /** What replacing this master's file would change. Writes nothing. */
  const previewReplacement = useCallback(
    async (masterId: string, file: File): Promise<ReplacementPreview> => {
      try {
        return await bopsApi.previewMasterReplacement(masterId, file);
      } catch (error) {
        throw new Error(messageFor(error, t('Configuration.Error')));
      }
    },
    [t],
  );

  /**
   * Write the file whose changes were approved.
   *
   * `contentHash` is what the preview returned for those bytes; the
   * server refuses any others, so what lands is what was read.
   */
  const replaceContent = useCallback(
    async (masterId: string, file: File, contentHash: string): Promise<void> => {
      try {
        await bopsApi.replaceMasterContent(masterId, file, contentHash);
      } catch (error) {
        throw new Error(messageFor(error, t('Configuration.Error')));
      }
    },
    [t],
  );

  return { createMaster, previewReplacement, replaceContent };
}
